// components/PasswordStrengthMeter.js
import React from 'react';
import {
  Progress,
  Text,
  VStack,
  Box,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  calculatePasswordStrength,
  getStrengthColor,
  getStrengthText,
} from '../utils/passwordValidation';

const RequirementItem = ({ met, text }) => {
  const textColor = useColorModeValue(
    met ? 'green.600' : 'gray.500',
    met ? 'green.300' : 'gray.400'
  );

  return (
    <HStack spacing={2}>
      <Box
        as="span"
        fontSize="xs"
        color={met ? 'green.500' : 'gray.400'}
        fontWeight="bold"
      >
        {met ? '✓' : '○'}
      </Box>
      <Text fontSize="xs" color={textColor}>
        {text}
      </Text>
    </HStack>
  );
};

export default function PasswordStrengthMeter({ password }) {
  if (!password || password.length === 0) {
    return null;
  }

  const strength = calculatePasswordStrength(password);
  const color = getStrengthColor(strength);
  const strengthText = getStrengthText(strength);

  const requirements = [
    { met: password.length >= 8, text: 'Au moins 8 caractères' },
    { met: password.length >= 12, text: '12 caractères ou plus' },
    { met: /[a-z]/.test(password) && /[A-Z]/.test(password), text: 'Majuscules & minuscules' },
    { met: /\d/.test(password), text: 'Au moins un chiffre' },
    { met: /[^a-zA-Z\d]/.test(password), text: 'Caractère spécial' },
  ];

  return (
    <VStack align="start" spacing={3} width="100%" mt={2}>
      {/* Barre de progression */}
      <Progress
        value={strength}
        size="sm"
        width="100%"
        colorScheme={color}
        borderRadius="full"
        hasStripe
        isAnimated={strength > 0}
      />
      
      {/* Légende force */}
      <HStack justify="space-between" width="100%">
        <Text fontSize="sm" fontWeight="medium" color={`${color}.500`}>
          Force: {strengthText}
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          {strength}%
        </Text>
      </HStack>

      {/* Exigences détaillées */}
      <VStack align="start" spacing={1} pl={1}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
          Exigences :
        </Text>
        {requirements.map((req, index) => (
          <RequirementItem key={index} met={req.met} text={req.text} />
        ))}
      </VStack>
    </VStack>
  );
}